import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AvailablePhoneNumber, AvailablePhoneNumbersService } from '../../../services/available-phone-numbers/available-phone-numbers.service';

/** US area code → state, used to group the numbers by location. Order of the states = display order. */
const AREA_CODE_STATES: { state: string; name: string; codes: string[] }[] = [
	{ state: 'TX', name: 'Texas', codes: ['214', '346', '210', '512'] },
	{ state: 'CA', name: 'California', codes: ['213', '619', '669', '510', '916', '650', '209', '408'] },
	{ state: 'NY', name: 'New York', codes: ['917', '929', '516', '716', '914'] },
	{ state: 'FL', name: 'Florida', codes: ['786', '813', '407', '904'] },
	{ state: 'PA', name: 'Pennsylvania', codes: ['215', '412'] },
	{ state: 'NJ', name: 'New Jersey', codes: ['973'] },
	{ state: 'IL', name: 'Illinois', codes: ['773'] },
	{ state: 'SC', name: 'South Carolina', codes: ['864'] },
];
const OTHER_STATE = 'Other';

export interface PhoneNumberGroup {
	state: string;
	/** Display label, e.g. "Texas (TX)". */
	label: string;
	numbers: AvailablePhoneNumber[];
}

function stateOf(phoneNumber: string): string {
	const digits = (phoneNumber ?? '').replace(/\D/g, '');
	const areaCode = digits.length === 11 && digits.startsWith('1') ? digits.slice(1, 4) : digits.slice(0, 3);
	return AREA_CODE_STATES.find((s) => s.codes.includes(areaCode))?.state ?? OTHER_STATE;
}

/**
 * Compact picker of the Vapi phone numbers — shown inside the agents' Configure modals. Every
 * number on the account is listed: green ones are free and can be picked, red ones can't — they're
 * either on the black list or already taken by another running agent instance.
 */
@Component({
	selector: 'app-available-phone-numbers',
	templateUrl: './available-phone-numbers.component.html',
	styleUrls: ['./available-phone-numbers.component.scss'],
})
export class AvailablePhoneNumbersComponent implements OnInit {
	/** phone_number_id currently picked in the parent modal (null = none yet). */
	@Input() selectedId: string | null = null;
	@Output() selectedIdChange = new EventEmitter<string | null>();
	/** Emits the full list every time it's (re)loaded from Vapi. */
	@Output() loaded = new EventEmitter<AvailablePhoneNumber[]>();

	phoneNumbers: AvailablePhoneNumber[] = [];
	/** phoneNumbers grouped by state (from the area code); unknown area codes go last under "Other". */
	groups: PhoneNumberGroup[] = [];
	/** States whose group is expanded (all collapsed by default; kept across refreshes). */
	expanded = new Set<string>();
	loading = false;
	error = false;

	constructor(private phoneNumbersSrv: AvailablePhoneNumbersService) {}

	ngOnInit(): void {
		this.loadPhoneNumbers();
	}

	get availableCount(): number {
		return this.phoneNumbers.filter((p) => p.available).length;
	}

	loadPhoneNumbers(): void {
		this.loading = true;
		this.error = false;
		this.phoneNumbersSrv.getAvailablePhoneNumbers().subscribe({
			next: (res: any) => {
				const rows = res?.body ?? res ?? [];
				// The API may send `available` as 1/0 or '1'/'0' — normalize to a real boolean.
				this.phoneNumbers = rows.map((r: any) => ({
					phone_number_id: r.phone_number_id,
					phone_number: r.phone_number,
					name: r.name ?? null,
					available: r.available === true || Number(r.available) === 1,
					unavailable_reason: r.unavailable_reason ?? null,
					blacklist_reason: r.blacklist_reason ?? null,
					assigned_agent_id: r.assigned_agent_id ?? null,
					assigned_agent_name: r.assigned_agent_name ?? null,
				}));
				this.groups = this.groupByState(this.phoneNumbers);
				this.loaded.emit(this.phoneNumbers);
				// The picked number may have been taken by someone else since the last load.
				if (this.selectedId && !this.phoneNumbers.some((p) => p.available && p.phone_number_id === this.selectedId)) {
					this.select(null);
				}
				this.loading = false;
			},
			error: () => {
				this.phoneNumbers = [];
				this.groups = [];
				this.error = true;
				this.loading = false;
				this.select(null);
			},
		});
	}

	pick(p: AvailablePhoneNumber): void {
		if (!p.available) return;
		this.select(p.phone_number_id);
	}

	toggleGroup(state: string): void {
		if (this.expanded.has(state)) this.expanded.delete(state);
		else this.expanded.add(state);
	}

	/** Free numbers in a group — shown in the header so it's useful even when collapsed. */
	freeCount(g: PhoneNumberGroup): number {
		return g.numbers.filter((p) => p.available).length;
	}

	/** True when the picked number is in this group — flagged in the header if the group is collapsed. */
	hasSelected(g: PhoneNumberGroup): boolean {
		return !!this.selectedId && g.numbers.some((p) => p.phone_number_id === this.selectedId);
	}

	chipTitle(p: AvailablePhoneNumber): string {
		const label = p.name ? `${p.name} · ` : '';
		if (p.available) return `${label}Available`;
		if (p.unavailable_reason === 'blacklisted') return `${label}Blacklisted${p.blacklist_reason ? ` — ${p.blacklist_reason}` : ''}`;
		return `${label}In use by ${p.assigned_agent_name ?? 'another agent'}`;
	}

	private groupByState(numbers: AvailablePhoneNumber[]): PhoneNumberGroup[] {
		const order = [...AREA_CODE_STATES.map((s) => ({ state: s.state, label: `${s.name} (${s.state})` })), { state: OTHER_STATE, label: OTHER_STATE }];
		return order
			.map(({ state, label }) => ({ state, label, numbers: numbers.filter((p) => stateOf(p.phone_number) === state) }))
			.filter((g) => g.numbers.length);
	}

	private select(id: string | null): void {
		if (this.selectedId === id) return;
		this.selectedId = id;
		this.selectedIdChange.emit(id);
	}
}
